/**
 * ADR-0248 §4.4 — one card for a probe-campaign (shown above the
 * sender list in the Suspicious segment).
 *
 * Reads like Sill voice (no exclamation marks, no emoji). Lead
 * sentence anchors on the dominant class + sender count + network;
 * the second line carries the first/last-seen rhythm; the third
 * line shows up to three sanitised example paths. Clicking the card
 * opens the detail panel.
 *
 * Severity colour comes from the server (`campaign.severity`); the
 * ShieldAlert icon mirrors the SenderCard shape so the two surfaces
 * read consistently.
 */

import type { CSSProperties, ReactNode } from 'react';

import { RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import type { ThreatCampaignSummary } from '@sill/types/api';
import { severityIcon } from './severity-icon.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface CampaignCardProps {
  readonly campaign: ThreatCampaignSummary;
  readonly isSelected: boolean;
  readonly onSelect: () => void;
}

export function CampaignCard({ campaign, isSelected, onSelect }: CampaignCardProps): JSX.Element {
  const C = useC();
  const sev = severityIcon(campaign.severity, C);
  const cardStyle: CSSProperties = {
    cursor: 'pointer',
    background: isSelected ? C.surface2 : C.surface,
    borderLeft: `2px solid ${isSelected ? C.limeFg : sev.colour}`,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
  };
  const second_line = `${campaign.request_count.toLocaleString('en-US')} request${campaign.request_count === 1 ? '' : 's'} · first seen `;
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="campaign-card"
      data-campaign-id={campaign.campaign_id}
      data-severity={campaign.severity}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
      }}
      aria-pressed={isSelected}
      aria-label={`${campaign.severity} probe campaign — ${campaign.headline}`}
      style={cardStyle}
    >
      <div
        data-testid="campaign-kicker"
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          letterSpacing: 2,
          color: C.muted,
          marginBottom: 8,
        }}
      >
        CAMPAIGN &middot; {campaign.dominant_class_label.toUpperCase()}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
        <Tooltip content={sev.tip}>
          <span data-testid="campaign-severity" aria-label={sev.tip} style={{ display: 'inline-flex', flexShrink: 0, marginTop: 2 }}>
            {sev.node as ReactNode}
          </span>
        </Tooltip>
        <span
          data-testid="campaign-headline"
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.text,
            fontWeight: 600,
            overflowWrap: 'anywhere',
            flex: 1,
            minWidth: 0,
            lineHeight: 1.35,
          }}
        >
          {campaign.headline}
        </span>
      </div>
      <div
        data-testid="campaign-time"
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          marginTop: 8,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
        }}
      >
        <span>{second_line}</span>
        <RelativeTime iso={campaign.first_seen} style={{ fontFamily: monoFont, fontSize: 10, color: C.body }} />
        <span>&middot; last</span>
        <RelativeTime iso={campaign.last_seen} style={{ fontFamily: monoFont, fontSize: 10, color: C.body }} />
      </div>
      {campaign.example_paths.length > 0 ? (
        <div
          data-testid="campaign-paths"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.body,
            marginTop: 8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {campaign.example_paths.slice(0, 3).join('  ·  ')}
        </div>
      ) : null}
      {campaign.ua_rotation_count !== null && campaign.ua_rotation_count > 0 ? (
        <div
          data-testid="campaign-rotation"
          style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 0.3, marginTop: 6 }}
        >
          {campaign.ua_rotation_count} user agents rotated across the campaign
        </div>
      ) : null}
    </div>
  );
}
